import { SITE } from "@/constants";
import { Helmet } from "react-helmet-async";

export default function MetaTags() {
  // Define full URL for images
  const baseUrl = SITE.url || "https://lexi.tips";
  // Ensure baseUrl doesn't end with a slash
  const baseUrlNormalized = baseUrl.endsWith("/")
    ? baseUrl.slice(0, -1)
    : baseUrl;
  const ogImageUrl = `${baseUrlNormalized}/images/lexi-social-share.jpg`; // This is the main social sharing image

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary Meta Tags */}
      <title>{SITE.title}</title>
      <meta name="title" content={SITE.title} />
      <meta name="description" content={SITE.description} />
      <link rel="canonical" href={SITE.url} />
      {/* Common Open Graph Meta Tags - High Priority for SEO */}
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={SITE.url} />
      <meta property="og:title" content={SITE.title} />
      <meta property="og:description" content={SITE.description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1920" />
      <meta property="og:image:height" content="1080" />
      <meta property="og:image:alt" content={`${SITE.name} - ${SITE.title}`} />
      <meta property="og:locale" content="en_AU" />
      {/* Twitter/X Card - High Priority for SEO */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@lexitips" />
      <meta name="twitter:creator" content="@lexitips" />
      <meta name="twitter:url" content={SITE.url} />
      <meta name="twitter:title" content={SITE.title} />
      <meta name="twitter:description" content={SITE.description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={`${SITE.name} - ${SITE.title}`} />
      {/* Facebook Specific */}
      <meta property="fb:app_id" content="YOUR_FACEBOOK_APP_ID" />{" "}
      {/* Replace with your Facebook App ID when available */}
      {/* LinkedIn */}
      <meta property="og:image:type" content="image/jpeg" />
      <meta name="author" content={SITE.author} />
      <meta name="linkedin:owner" content={SITE.author} />
      {/* Additional meta tags */}
      <meta name="application-name" content={SITE.name} />
      <meta name="apple-mobile-web-app-title" content={SITE.name} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="msapplication-TileColor" content={SITE.themeColor} />
      <meta name="theme-color" content={SITE.themeColor} />
      {/* Favicon */}
      <link rel="icon" href="/icons/favicon.ico" />
      {/* Apple Touch Icons - separate from social media meta tags */}
      <link
        rel="apple-touch-icon"
        sizes="57x57"
        href="/icons/apple-touch-icon-57x57.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="72x72"
        href="/icons/apple-touch-icon-72x72.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="76x76"
        href="/icons/apple-touch-icon-76x76.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="114x114"
        href="/icons/apple-touch-icon-114x114.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="120x120"
        href="/icons/apple-touch-icon-120x120.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="144x144"
        href="/icons/apple-touch-icon-144x144.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="152x152"
        href="/icons/apple-touch-icon-152x152.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/icons/apple-touch-icon-180x180.png"
      />
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "${SITE.url}",
            "name": "${SITE.name}",
            "description": "${SITE.description}",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "${SITE.url}/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        `}
      </script>
      {/* Organization Schema */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "${SITE.name}",
            "url": "${SITE.url}",
            "logo": "${baseUrlNormalized}/assets/lexi-tips-logo.svg",
            "description": "${SITE.description}",
            "sameAs": [
              "https://www.instagram.com/lexi.tips/",
              "https://x.com/Lexi_Tips",
              "https://www.facebook.com/LexiDotTips"
            ]
          }
        `}
      </script>
      {/* BreadcrumbList Schema - This provides better search results appearance */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "${SITE.url}"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Horse Racing Tips",
                "item": "${SITE.url}/tips"
              }
            ]
          }
        `}
      </script>
      {/* SportingEvent Schema - Updated for Google Search Console compliance */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "SportsEvent",
            "name": "Horse Racing Tips & Analysis",
            "description": "${SITE.description}",
            "sport": "Horse Racing",
            "startDate": "${new Date().toISOString().split("T")[0]}T12:00:00+10:00",
            "endDate": "${new Date().toISOString().split("T")[0]}T18:00:00+10:00",
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/MixedEventAttendanceMode",
            "image": "${ogImageUrl}",
            "location": [
              {
                "@type": "Place",
                "name": "Australian Racecourses",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Melbourne",
                  "addressRegion": "VIC",
                  "postalCode": "3000",
                  "addressCountry": "AU"
                }
              },
              {
                "@type": "VirtualLocation",
                "url": "${SITE.url}/todays-tips"
              }
            ],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "AUD",
              "availability": "https://schema.org/InStock",
              "validFrom": "${new Date().toISOString().split("T")[0]}T00:00:00+10:00"
            }
          }
        `}
      </script>
    </Helmet>
  );
}
